import { useTranslation } from 'react-i18next';
import { RotateCcw, Search } from 'lucide-react';
import {
  Input,
  TableDateRangePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui';
import {
  DEFAULT_FILTERS,
  getPeriodDates,
  SERVICE_OPERATION_TYPES,
  SERVICE_WALLET_KINDS,
  toggleType,
  toggleWallet,
  WALLET_KIND_LABEL_KEYS,
  type PeriodPreset,
  type ServiceAccountingFilters,
  type ServiceSortOption,
} from '@entities/service-accounting';
import { cn } from '@shared/lib/utils';

interface FiltersToolbarProps {
  filters: ServiceAccountingFilters;
  onChange: (next: ServiceAccountingFilters) => void;
}

const TYPE_OPTIONS = SERVICE_OPERATION_TYPES;
const WALLET_OPTIONS = SERVICE_WALLET_KINDS;
const PERIOD_OPTIONS: Array<Exclude<PeriodPreset, null>> = ['today', '7d', '30d', 'all'];
const SORT_OPTIONS: ServiceSortOption[] = ['date_desc', 'date_asc', 'amount_desc', 'amount_asc'];

const chipClassName = (active: boolean) =>
  cn(
    'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-fast ease-standard sm:min-h-0 sm:text-sm',
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-border text-muted-foreground hover:bg-accent'
  );

export function FiltersToolbar({ filters, onChange }: FiltersToolbarProps) {
  const { t } = useTranslation('cabinet');

  const handleTypeToggle = (type: (typeof SERVICE_OPERATION_TYPES)[number]) => {
    onChange({ ...filters, types: toggleType(filters.types, type) });
  };

  const handleWalletToggle = (wallet: (typeof SERVICE_WALLET_KINDS)[number]) => {
    onChange({ ...filters, wallets: toggleWallet(filters.wallets, wallet) });
  };

  const handlePeriodSelect = (period: Exclude<PeriodPreset, null>) => {
    if (filters.period === period) {
      onChange({ ...filters, period: null, dateFrom: '', dateTo: '' });
      return;
    }
    const { dateFrom, dateTo } = getPeriodDates(period);
    onChange({ ...filters, period, dateFrom, dateTo });
  };

  const handleReset = () => {
    onChange(DEFAULT_FILTERS);
  };

  return (
    <div className="surface-card space-y-3 sm:space-y-4">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="w-full sm:w-64">
          <Input
            icon={<Search className="h-4 w-4" />}
            placeholder={t('serviceAccounting.filters.searchPlaceholder')}
            value={filters.search}
            onChange={(event) => onChange({ ...filters, search: event.target.value })}
          />
        </div>

        <TableDateRangePicker
          className="w-full sm:min-w-[220px] sm:flex-1"
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          placeholder={t('serviceAccounting.filters.periodLabel')}
          onChange={({ dateFrom, dateTo }) =>
            onChange({ ...filters, dateFrom, dateTo, period: null })
          }
        />

        <Select
          value={filters.sort}
          onValueChange={(value) => onChange({ ...filters, sort: value as ServiceSortOption })}
        >
          <SelectTrigger className="w-full basis-full sm:w-48 sm:basis-auto">
            <SelectValue placeholder={t('serviceAccounting.sort.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option} value={option}>
                {t(`serviceAccounting.sort.${option}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button
          type="button"
          onClick={handleReset}
          className="flex min-h-tap shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium text-muted-foreground transition-colors duration-fast ease-standard hover:bg-accent sm:min-h-0 sm:text-sm"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {t('serviceAccounting.filters.reset')}
        </button>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">{t('serviceAccounting.filters.typesLabel')}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {TYPE_OPTIONS.map((type) => (
            <button
              key={type}
              type="button"
              className={chipClassName(filters.types.includes(type))}
              onClick={() => handleTypeToggle(type)}
            >
              {t(`serviceAccounting.types.${type}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">{t('serviceAccounting.filters.walletsLabel')}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {WALLET_OPTIONS.map((wallet) => (
            <button
              key={wallet}
              type="button"
              className={chipClassName(filters.wallets.includes(wallet))}
              onClick={() => handleWalletToggle(wallet)}
            >
              {t(WALLET_KIND_LABEL_KEYS[wallet])}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">{t('serviceAccounting.filters.periodLabel')}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {PERIOD_OPTIONS.map((period) => (
            <button
              key={period}
              type="button"
              className={chipClassName(filters.period === period)}
              onClick={() => handlePeriodSelect(period)}
            >
              {t(`serviceAccounting.periods.${period}`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
